import Box from "@mui/material/Box";
import { useMemo, useRef, useState } from "react";
import { BarChartColors } from "../../../../../../components/charts/constants";
import { useGetMarketSize } from "../../../../hooks/useGetMarketSize";
import { useGetCompetitorMarketPenetration } from "./hooks/useGetCompetitorMarketPenetration";
import useMatchProductsById from "../../../MarketLandscape/hooks/useMatchProductsById";
import SimpleColumnChart from "../../../../../../components/charts/SimpleColumnChart/SimpleColumnChart";
import { useWorkspaceStore } from "../../../../../../store/workspace/WorkspaceStore";
import {
  determineChartContent,
  formatPenetrationPercentage,
  hasNoData,
  safeCalculatePercentage,
  isValidChartValue,
} from "../../../../utils";
import * as am4charts from "@amcharts/amcharts4/charts";
import { ChartExportMenu } from "../../../../../../components/charts/ChartExportMenu";
import { Market } from "../../../../../../types/workspace";
import { TitleWithTooltipInfo } from "../../../../../../components/charts/TitleWithTooltipInfo";
import { CHART_CONSTANTS } from "../../../constants";

const { chartTitle, baseId, csvFieldMappings, tooltip, topProducts } =
  CHART_CONSTANTS.competitorMarketPenetration;

export const CompetitorMarketPenetration = () => {
  const [isChartHovered, setIsChartHovered] = useState(false);
  const chartRef = useRef<am4charts.XYChart>(null);
  const { marketPenetrationData, isLoading: marketPenetrationLoading } =
    useGetCompetitorMarketPenetration();

  const selectedMarket = useWorkspaceStore((state) => state.selectedMarket);
  const competitorsProductIds = useWorkspaceStore(
    (state) => state.draft?.configuration.competitorsProductIds || []
  );

  const { tam, sam, som } = useGetMarketSize();

  const selectedMarketSize = useMemo(
    () => ({ tam, sam, som })[selectedMarket || Market.TAM],
    [sam, selectedMarket, som, tam]
  );

  const isLoading = marketPenetrationLoading || selectedMarketSize.loading;

  const { productsNameMapById, matchedProductsById } = useMatchProductsById(
    competitorsProductIds
  );

  const chartData = useMemo(() => {
    // Filter out items with invalid company counts first
    const validMarketData = marketPenetrationData.filter(
      (item) => item.companyCount > 0 && Number.isFinite(item.companyCount)
    );

    const topProductIds = new Set(
      [...validMarketData]
        .sort(
          ({ companyCount: countA }, { companyCount: countB }) =>
            countB - countA
        )
        .slice(0, topProducts)
        .map((item) => item.productId)
    );

    return validMarketData
      .filter((item) => topProductIds.has(item.productId))
      .map((item) => {
        const penetrationPercentage = safeCalculatePercentage(
          item.companyCount,
          selectedMarketSize.companyCount
        );
        return {
          productName: productsNameMapById[item.productId] || "Unknown",
          companyCount: item.companyCount,
          percentage: penetrationPercentage,
          formattedPercentage: formatPenetrationPercentage(
            penetrationPercentage
          ),
          vendorName:
            matchedProductsById.find((product) => product.id === item.productId)
              ?.vendor?.name || "Unknown",
          categoryName:
            matchedProductsById.find((product) => product.id === item.productId)
              ?.category?.name || "Unknown",
        };
      })
      .filter((item) => isValidChartValue(item.percentage)) // Filter out invalid percentages
      .sort((a, b) => b.companyCount - a.companyCount);
  }, [
    selectedMarketSize.companyCount,
    marketPenetrationData,
    productsNameMapById,
    matchedProductsById,
  ]);

  const noDataReturned = useMemo(
    () => hasNoData(isLoading, chartData),
    [chartData, isLoading]
  );
  const shouldShowExport = chartRef && !noDataReturned && !isLoading;

  const chartContent = useMemo(() => {
    return determineChartContent({
      productIds: competitorsProductIds,
      isLoading,
      noDataReturned,
      baseId,
      chartToRender: (
        <Box id={`${baseId}-chart-wrapper`} height="364px">
          <SimpleColumnChart
            id={`${baseId}-chart`}
            data={chartData}
            xAxisDataPoint="productName"
            xAxisTooltipFormat={"{productName}\n{vendorName}\n{categoryName}"}
            yAxisDataPoint="companyCount"
            columnLabelFormat="{formattedPercentage}%"
            columnTooltipFormat={
              "{productName}:\n[bold]{companyCount}\n{formattedPercentage}%[/]"
            }
            xAxisLabel="Competitive Products"
            yAxisLabel="# of Companies"
            barColor={BarChartColors}
            truncateXAxisLabels
            ref={chartRef}
          />
        </Box>
      ),
    });
  }, [chartData, isLoading, noDataReturned, competitorsProductIds]);

  return (
    <Box
      id={`${baseId}-wrapper`}
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      height="390px"
      borderRadius="10px"
      padding="22.3px 20px 19px"
      border="solid 1px #edeff1"
      gap="25px"
      bgcolor={
        noDataReturned || competitorsProductIds.length === 0
          ? "#edeff1"
          : "#fff"
      }
      onMouseEnter={() => setIsChartHovered(true)}
      onMouseLeave={() => setIsChartHovered(false)}
    >
      <Box display="flex" justifyContent="space-between">
        <TitleWithTooltipInfo
          tooltipTitle={tooltip}
          chartTitle={chartTitle}
          showInfoIcon={isChartHovered}
        />
        {shouldShowExport && (
          <ChartExportMenu
            chart={chartRef}
            title={chartTitle}
            csvFieldMappings={csvFieldMappings}
          />
        )}
      </Box>
      {chartContent}
    </Box>
  );
};
